import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { ExamPaperQuestionItem, ExamPaperDetail, updateExamPaperQuestion, updateExamGroup } from '@/api/exam';

const GROUP_TYPES = [
  { value: 'reading_comp', label: '阅读理解' },
  { value: 'cloze', label: '完形填空' },
  { value: 'matching', label: '信息匹配' },
  { value: 'seven_choose_five', label: '七选五' },
  { value: 'grammar_fill', label: '语法填空' },
  { value: 'listening', label: '听力' },
]

interface GroupQuestionEditorProps {
  groupId: string;
  groupType: string;
  passageContent: string;
  groupOptions: { label: string; content: string }[];
  subQuestions: ExamPaperQuestionItem[];
  onUpdate: (updatedPaper: ExamPaperDetail) => void;
  onCancel: () => void;
}

export const GroupQuestionEditor: React.FC<GroupQuestionEditorProps> = ({
  groupId,
  groupType: initialGroupType,
  passageContent: initialPassage,
  groupOptions: initialOptions,
  subQuestions: initialSubQuestions,
  onUpdate,
  onCancel,
}) => {
  const [groupType, setGroupType] = useState(initialGroupType)
  const [passage, setPassage] = useState(initialPassage)
  const [options, setOptions] = useState<{ label: string; content: string }[]>(initialOptions)
  const [subQuestions, setSubQuestions] = useState(initialSubQuestions)
  const [saving, setSaving] = useState(false)
  const [selectedSubIdx, setSelectedSubIdx] = useState<number | null>(null)

  // Update local state when props change
  useEffect(() => {
    setGroupType(initialGroupType)
    setPassage(initialPassage)
    setOptions(initialOptions)
    setSubQuestions(initialSubQuestions)
  }, [groupId])

  const selectedSub = selectedSubIdx !== null ? subQuestions[selectedSubIdx] : null

  const handleAddOption = () => {
    const nextLabel = String.fromCharCode(65 + options.length)
    setOptions([...options, { label: nextLabel, content: '' }])
  }

  const handleRemoveOption = (idx: number) => {
    const newOptions = options.filter((_, i) => i !== idx)
    // Re-label
    newOptions.forEach((opt, i) => { opt.label = String.fromCharCode(65 + i) })
    setOptions(newOptions)
  }

  const handleUpdateOption = (idx: number, content: string) => {
    const newOptions = [...options]
    newOptions[idx] = { ...newOptions[idx], content }
    setOptions(newOptions)
  }

  const handleUpdateSubQuestion = (idx: number, field: string, value: any) => {
    const newSubs = [...subQuestions]
    const q = { ...newSubs[idx] }
    if (field === 'stem') {
      q.content = { ...q.content, stem: value }
    } else if (field === 'answer') {
      q.answer = { value }
    } else if (field === 'type') {
      q.type = value
    }
    newSubs[idx] = q
    setSubQuestions(newSubs)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Save group metadata (type, passage, options)
      await updateExamGroup(groupId, {
        type: groupType,
        passage_content: passage,
        options: options.length > 0 ? options : null,
      })

      // Save each sub-question
      for (const sq of subQuestions) {
        await updateExamPaperQuestion(sq.question_id, {
          content: sq.content as any,
          type: sq.type ?? undefined,
          answer: sq.answer as any,
          options: sq.options as any,
        })
      }

      toast.success('题组已更新')
    } catch (error: any) {
      toast.error(error.message || '保存失败')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between sticky top-0 bg-white z-10 py-2 border-b">
        <h3 className="font-medium text-lg">编辑题组</h3>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onCancel}>取消</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            保存修改
          </Button>
        </div>
      </div>

      {/* Group type */}
      <div>
        <label className="text-sm font-medium mb-1 block">题组类型</label>
        <Select value={groupType} onValueChange={setGroupType}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GROUP_TYPES.map(gt => (
              <SelectItem key={gt.value} value={gt.value}>{gt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Passage */}
      <div>
        <label className="text-sm font-medium mb-1 block">
          {groupType === 'matching' ? '选项描述' : '文章/段落内容'}
        </label>
        <Textarea
          value={passage}
          onChange={(e) => setPassage(e.target.value)}
          className="min-h-[150px]"
          placeholder={groupType === 'matching' ? 'A. 选项一描述...\nB. 选项二描述...' : '在此输入文章内容...'}
        />
      </div>

      {/* Shared options pool (for matching, seven_choose_five) */}
      {(groupType === 'matching' || groupType === 'seven_choose_five') && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">共享选项</label>
            <Button variant="ghost" size="sm" onClick={handleAddOption} className="h-7 px-2 text-xs">
              <Plus className="h-3 w-3 mr-1" /> 添加选项
            </Button>
          </div>
          {options.map((opt, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center bg-muted rounded text-sm font-bold shrink-0">
                {opt.label}
              </span>
              <Input
                value={opt.content}
                onChange={(e) => handleUpdateOption(idx, e.target.value)}
                className="flex-1"
                placeholder={`选项 ${opt.label} 内容`}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => handleRemoveOption(idx)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Sub-questions */}
      <div className="space-y-2">
        <label className="text-sm font-medium">子题列表 ({subQuestions.length})</label>
        <div className="border rounded-lg divide-y">
          {subQuestions.map((sq, idx) => {
            const isSelected = selectedSubIdx === idx
            const stem = (sq.content as any)?.stem || ''
            return (
              <div key={sq.question_id}>
                <div
                  onClick={() => setSelectedSubIdx(isSelected ? null : idx)}
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                >
                  <span className="text-sm font-medium text-muted-foreground w-6">{idx + 1}.</span>
                  <span className="text-sm flex-1 truncate">{stem || '(空)'}</span>
                  {sq.answer?.value && (
                    <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                      答案: {sq.answer.value}
                    </span>
                  )}
                </div>

                {/* Expanded edit form */}
                {isSelected && (
                  <div className="px-3 pb-3 space-y-3 bg-muted/20">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">题干</label>
                      <Textarea
                        value={stem}
                        onChange={(e) => handleUpdateSubQuestion(idx, 'stem', e.target.value)}
                        className="min-h-[80px] text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">题型</label>
                        <Select
                          value={sq.type || 'choice'}
                          onValueChange={(v) => handleUpdateSubQuestion(idx, 'type', v)}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="choice">选择题</SelectItem>
                            <SelectItem value="essay">解答题</SelectItem>
                            <SelectItem value="fill_blank">填空题</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">正确答案</label>
                        <Input
                          value={sq.answer?.value || ''}
                          onChange={(e) => handleUpdateSubQuestion(idx, 'answer', e.target.value)}
                          className="h-8 text-sm"
                          placeholder="如: A"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
